/**
 * Email layout templates (local only — not persisted to DB yet).
 * Each template provides a wireframe HTML skeleton for AI generation and a UI preview.
 *
 * Templates are grouped by `category` so the picker can be filtered for different
 * audiences (e-commerce, SaaS launches, content newsletters, etc.).
 */

import { bulletproofCtaHtml } from './bulletproof-cta';

export type TemplateCategory =
  | 'ecommerce'
  | 'product'
  | 'newsletter'
  | 'creator'
  | 'course'
  | 'general';

export type TemplateCopyDensity = 'short' | 'medium' | 'dense';
export type TemplateImageStrategy = 'hero' | 'grid' | 'thumbnail' | 'none';

export interface EmailLayoutTemplateMetadata {
  audienceType: string;
  campaignGoal: string;
  campaignType: string;
  recommendedStyles: string[];
  requiredSections: string[];
  optionalSections: string[];
  defaultCtaIntent: string;
  copyDensity: TemplateCopyDensity;
  imageStrategy: TemplateImageStrategy;
}

export interface EmailLayoutTemplate {
  id: string;
  label: string;
  description: string;
  category: TemplateCategory;
  /** Short structural guidance merged into the user prompt. */
  layoutPrompt: string;
  /** Minimal table-based wireframe passed to /api/generate as starter_html. */
  starterHtml: string;
  /** Tiny HTML thumbnail for the template picker card. */
  previewHtml: string;
  /** Structured generation context passed to the backend prompt builder. */
  metadata?: EmailLayoutTemplateMetadata;
}

/** User opts out of layout wireframe — AI follows prompt only. */
export const LAYOUT_TEMPLATE_NONE_ID = 'none' as const;

/** Category metadata for filter chips and per-card badges. */
export const TEMPLATE_CATEGORIES: Array<{
  id: TemplateCategory;
  label: string;
  description: string;
}> = [
  { id: 'ecommerce', label: 'E-commerce', description: 'Sales, products, welcome' },
  { id: 'product', label: 'Product/SaaS', description: 'Launches, updates, onboarding' },
  { id: 'newsletter', label: 'Newsletter', description: 'Daily briefs and digests' },
  { id: 'creator', label: 'Creator', description: 'Personal updates and memberships' },
  { id: 'course', label: 'Event/Course', description: 'Invites, enrollment, lessons' },
  { id: 'general', label: 'General', description: 'Broadly applicable layouts' },
];

export function getCategoryLabel(category: TemplateCategory): string {
  return TEMPLATE_CATEGORIES.find((c) => c.id === category)?.label ?? category;
}

// ---------------------------------------------------------------------------
// Shared wireframe helpers
// ---------------------------------------------------------------------------

const ECOMMERCE_LAYOUT_PREFIX =
  'This is an e-commerce promotional email. Include a clear offer, product imagery placeholders, strikethrough original price where relevant, one primary CTA above the fold, and a CAN-SPAM footer (unsubscribe, address). ';

const GENERAL_LAYOUT_PREFIX =
  'Include a clear primary CTA above the fold, plausible on-brand placeholder copy (never lorem ipsum), and a footer with unsubscribe link and brand info. ';

const PRODUCT_LAYOUT_PREFIX =
  'This is a product or SaaS lifecycle email. Make the value proposition specific, connect features to user outcomes, keep the CTA action-oriented, and avoid sounding like a generic corporate announcement. ';

const NEWSLETTER_LAYOUT_PREFIX =
  'This is a newsletter or brief. Prioritize scanability, clear hierarchy, useful summaries, and reader trust. Do not turn it into a discount-led promo unless the user explicitly asks. ';

const COURSE_LAYOUT_PREFIX =
  'This is an event, webinar, course, or knowledge-commerce email. Make the promise concrete, include practical details, reduce hesitation with proof or agenda points, and use one clear registration/enrollment CTA. ';

const WRAPPER = (body: string) =>
  `<!doctype html><html><head><meta charset="utf-8"/></head><body style="margin:0;padding:0;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">${body}</body></html>`;

const BRAND_HEADER =
  '<tr><td style="padding:20px 24px 12px;text-align:center;font-size:15px;font-weight:700;color:#18181b;letter-spacing:0.06em;">BRAND</td></tr>';

const TRUST_BADGES = `<tr><td style="padding:18px 24px;background:#fafafa;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr>
    <td width="33%" style="text-align:center;font-size:11px;color:#52525b;line-height:1.5;"><strong style="color:#18181b;">Free shipping</strong><br/><span style="color:#a1a1aa;">orders over $50</span></td>
    <td width="33%" style="text-align:center;font-size:11px;color:#52525b;line-height:1.5;"><strong style="color:#18181b;">30-day returns</strong><br/><span style="color:#a1a1aa;">no questions</span></td>
    <td width="34%" style="text-align:center;font-size:11px;color:#52525b;line-height:1.5;"><strong style="color:#18181b;">Secure checkout</strong><br/><span style="color:#a1a1aa;">SSL &amp; 3DS</span></td>
  </tr></table>
</td></tr>`;

const FOOTER =
  '<tr><td style="padding:22px 24px;border-top:1px solid #e4e4e7;text-align:center;font-size:11px;color:#a1a1aa;line-height:1.7;">Brand Co. · 123 Address St, City, ZIP<br/><a href="#" style="color:#a1a1aa;">Unsubscribe</a> · <a href="#" style="color:#a1a1aa;">Preferences</a> · <a href="#" style="color:#a1a1aa;">View in browser</a></td></tr>';

const SIMPLE_PREVIEW = `
<div style="display:flex;flex-direction:column;gap:3px;height:100%;padding:6px;background:#fafafa;">
  <div style="height:6%;background:#d4d4d8;border-radius:2px;margin:0 34%;"></div>
  <div style="height:9%;background:#18181b;border-radius:2px;margin:2px 10% 0;"></div>
  <div style="height:5%;background:#e4e4e7;border-radius:2px;margin:0 18%;"></div>
  <div style="height:24%;background:#e4e4e7;border-radius:3px;"></div>
  <div style="height:5%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="height:5%;background:#e4e4e7;border-radius:2px;width:78%;"></div>
  <div style="height:10%;background:#e85d4c;border-radius:3px;margin:6px 30% 0;"></div>
</div>`;

const promoCodeBox = (code: string, label: string) => `<tr><td style="padding:4px 24px 20px;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr>
    <td style="padding:16px 20px;text-align:center;border:2px dashed #d4d4d8;border-radius:8px;background:#fafafa;">
      <div style="font-size:11px;color:#71717a;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:6px;">${label}</div>
      <div style="font-size:22px;font-weight:700;color:#18181b;letter-spacing:0.1em;">${code}</div>
    </td>
  </tr></table>
</td></tr>`;

export const EMAIL_LAYOUT_TEMPLATES: EmailLayoutTemplate[] = [
  // ---------------------------------------------------------------------------
  // E-COMMERCE
  // ---------------------------------------------------------------------------
  {
    id: 'flash-sale',
    label: 'Flash sale',
    description: 'Promo bar, big discount hero, code box + trust badges — sitewide sales.',
    category: 'ecommerce',
    layoutPrompt:
      ECOMMERCE_LAYOUT_PREFIX +
      'Use a top promo bar (offer + urgency), brand wordmark, full-width campaign hero image, oversized discount headline (e.g. "40% OFF"), short urgency/value line, a dashed bordered promo code box, prominent primary CTA, a 3-column trust badges row (free shipping, returns, secure checkout), then footer. Linear flow: urgency → offer → action → reassurance.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  <tr><td style="padding:12px 24px;text-align:center;background:#18181b;color:#ffffff;font-size:12px;font-weight:600;letter-spacing:0.04em;">FREE SHIPPING ON ORDERS $50+ · ENDS SUNDAY</td></tr>
  ${BRAND_HEADER}
  <tr><td style="padding:0;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:240px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;">Campaign hero image</td></tr></table></td></tr>
  <tr><td style="padding:32px 28px 6px;text-align:center;"><h1 style="margin:0;font-size:40px;line-height:1.05;color:#18181b;font-weight:800;letter-spacing:-0.02em;">40% OFF EVERYTHING</h1></td></tr>
  <tr><td style="padding:6px 28px 16px;text-align:center;"><p style="margin:0;font-size:15px;line-height:1.5;color:#52525b;">Our biggest sale of the season — shop bestsellers before they sell out.</p></td></tr>
  ${promoCodeBox('EXTRA20', 'Use code at checkout')}
  <tr><td style="padding:4px 28px 28px;text-align:center;">${bulletproofCtaHtml('Shop the sale')}</td></tr>
  ${TRUST_BADGES}
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;gap:3px;height:100%;padding:6px;background:#fafafa;font-size:0;">
  <div style="height:6%;background:#18181b;border-radius:2px;"></div>
  <div style="height:4%;background:#d4d4d8;border-radius:2px;margin:0 30%;"></div>
  <div style="height:28%;background:#e4e4e7;border-radius:3px;"></div>
  <div style="height:10%;background:#18181b;border-radius:2px;margin:0 8%;"></div>
  <div style="height:14%;border:2px dashed #d4d4d8;border-radius:3px;background:#fafafa;margin:2px 18%;"></div>
  <div style="height:9%;background:#e85d4c;border-radius:3px;margin:0 28%;"></div>
  <div style="height:7%;display:flex;gap:2px;margin-top:auto;"><div style="flex:1;background:#e4e4e7;border-radius:1px;"></div><div style="flex:1;background:#e4e4e7;border-radius:1px;"></div><div style="flex:1;background:#e4e4e7;border-radius:1px;"></div></div>
</div>`,
  },
  {
    id: 'product-grid-sale',
    label: 'Product grid',
    description: '2×2 product cards with sale badges and pricing — collections and bestsellers.',
    category: 'ecommerce',
    layoutPrompt:
      ECOMMERCE_LAYOUT_PREFIX +
      'Use a brand wordmark header, short promo headline (e.g. "Up to 30% off bestsellers"), one-line benefit. Then a 2×2 product grid: each cell has image, small badge (SALE / -30% / NEW / BESTSELLER), product name, strikethrough original price + sale price (use the SAME structure for all four cards — consistency matters), and Shop now link. End with one full-width "Shop all" CTA and footer.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:6px 24px 4px;text-align:center;"><h2 style="margin:0;font-size:22px;font-weight:700;color:#18181b;">Up to 30% off bestsellers</h2></td></tr>
  <tr><td style="padding:0 24px 20px;text-align:center;font-size:14px;color:#52525b;">Our most-loved styles, marked down for a limited time.</td></tr>
  <tr><td style="padding:0 16px;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td width="50%" style="padding:8px;vertical-align:top;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr><td style="height:140px;background:#e4e4e7;font-size:11px;color:#71717a;text-align:center;border-radius:6px;">Image</td></tr>
          <tr><td style="padding:10px 4px 2px;font-size:11px;font-weight:700;color:#e85d4c;letter-spacing:0.06em;">-30% SALE</td></tr>
          <tr><td style="padding:0 4px 2px;font-size:14px;font-weight:600;color:#18181b;">Product A</td></tr>
          <tr><td style="padding:0 4px 4px;font-size:13px;color:#52525b;"><span style="text-decoration:line-through;color:#a1a1aa;">$69</span> &nbsp;<strong style="color:#18181b;">$49</strong></td></tr>
          <tr><td style="padding:0 4px 8px;font-size:12px;"><a href="#" style="color:#e85d4c;font-weight:600;">Shop now →</a></td></tr>
        </table>
      </td>
      <td width="50%" style="padding:8px;vertical-align:top;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr><td style="height:140px;background:#e4e4e7;font-size:11px;color:#71717a;text-align:center;border-radius:6px;">Image</td></tr>
          <tr><td style="padding:10px 4px 2px;font-size:11px;font-weight:700;color:#e85d4c;letter-spacing:0.06em;">-25% SALE</td></tr>
          <tr><td style="padding:0 4px 2px;font-size:14px;font-weight:600;color:#18181b;">Product B</td></tr>
          <tr><td style="padding:0 4px 4px;font-size:13px;color:#52525b;"><span style="text-decoration:line-through;color:#a1a1aa;">$79</span> &nbsp;<strong style="color:#18181b;">$59</strong></td></tr>
          <tr><td style="padding:0 4px 8px;font-size:12px;"><a href="#" style="color:#e85d4c;font-weight:600;">Shop now →</a></td></tr>
        </table>
      </td>
    </tr><tr>
      <td width="50%" style="padding:8px;vertical-align:top;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr><td style="height:140px;background:#e4e4e7;font-size:11px;color:#71717a;text-align:center;border-radius:6px;">Image</td></tr>
          <tr><td style="padding:10px 4px 2px;font-size:11px;font-weight:700;color:#92400e;letter-spacing:0.06em;">BESTSELLER</td></tr>
          <tr><td style="padding:0 4px 2px;font-size:14px;font-weight:600;color:#18181b;">Product C</td></tr>
          <tr><td style="padding:0 4px 4px;font-size:13px;color:#52525b;"><span style="text-decoration:line-through;color:#a1a1aa;">$59</span> &nbsp;<strong style="color:#18181b;">$39</strong></td></tr>
          <tr><td style="padding:0 4px 8px;font-size:12px;"><a href="#" style="color:#e85d4c;font-weight:600;">Shop now →</a></td></tr>
        </table>
      </td>
      <td width="50%" style="padding:8px;vertical-align:top;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr><td style="height:140px;background:#e4e4e7;font-size:11px;color:#71717a;text-align:center;border-radius:6px;">Image</td></tr>
          <tr><td style="padding:10px 4px 2px;font-size:11px;font-weight:700;color:#18181b;letter-spacing:0.06em;">NEW</td></tr>
          <tr><td style="padding:0 4px 2px;font-size:14px;font-weight:600;color:#18181b;">Product D</td></tr>
          <tr><td style="padding:0 4px 4px;font-size:13px;color:#52525b;"><span style="text-decoration:line-through;color:#a1a1aa;">$65</span> &nbsp;<strong style="color:#18181b;">$45</strong></td></tr>
          <tr><td style="padding:0 4px 8px;font-size:12px;"><a href="#" style="color:#e85d4c;font-weight:600;">Shop now →</a></td></tr>
        </table>
      </td>
    </tr></table>
  </td></tr>
  <tr><td style="padding:16px 24px 28px;text-align:center;">${bulletproofCtaHtml('Shop all products', '#', '#18181b')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;gap:3px;height:100%;padding:6px;background:#fafafa;">
  <div style="height:5%;background:#d4d4d8;border-radius:2px;margin:0 35%;"></div>
  <div style="height:6%;background:#18181b;border-radius:2px;margin:0 18%;"></div>
  <div style="flex:1;display:grid;grid-template-columns:1fr 1fr;gap:3px;">
    <div style="background:#e4e4e7;border-radius:2px;"></div><div style="background:#e4e4e7;border-radius:2px;"></div>
    <div style="background:#e4e4e7;border-radius:2px;"></div><div style="background:#e4e4e7;border-radius:2px;"></div>
  </div>
  <div style="height:9%;background:#18181b;border-radius:2px;margin:2px 25% 0;"></div>
</div>`,
  },
  {
    id: 'single-sku-spotlight',
    label: 'SKU spotlight',
    description: 'Hero product with star rating, bullets, price — new arrivals and bestsellers.',
    category: 'ecommerce',
    layoutPrompt:
      ECOMMERCE_LAYOUT_PREFIX +
      'Use a brand wordmark header, small eyebrow tag (NEW ARRIVAL / BESTSELLER / LIMITED), large product hero image, product name headline, 5-star rating row with review count (e.g. "★★★★★ 4.8 · 1,234 reviews"), 3 short benefit bullets, price block (strikethrough original + bold current), primary Buy now CTA, then a free-shipping / easy-returns trust line and footer. Feels like a D2C product detail page collapsed into an email.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:0 28px 10px;text-align:center;"><span style="display:inline-block;padding:5px 12px;background:#fef3c7;color:#92400e;font-size:11px;font-weight:700;border-radius:999px;letter-spacing:0.08em;">NEW ARRIVAL</span></td></tr>
  <tr><td style="padding:0 28px 16px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:280px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;border-radius:6px;">Product hero image</td></tr></table></td></tr>
  <tr><td style="padding:0 28px 6px;text-align:center;"><h1 style="margin:0;font-size:26px;line-height:1.2;color:#18181b;font-weight:700;">Product name — one line hook</h1></td></tr>
  <tr><td style="padding:0 28px 14px;text-align:center;font-size:13px;color:#52525b;"><span style="color:#f59e0b;letter-spacing:0.08em;">★★★★★</span> &nbsp;<strong>4.8</strong> · 1,234 reviews</td></tr>
  <tr><td style="padding:0 28px 12px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="font-size:14px;color:#52525b;line-height:1.7;">
      <tr><td style="padding:5px 0;">✓ &nbsp; Key benefit one explained briefly</td></tr>
      <tr><td style="padding:5px 0;">✓ &nbsp; Key benefit two explained briefly</td></tr>
      <tr><td style="padding:5px 0;">✓ &nbsp; Key benefit three explained briefly</td></tr>
    </table>
  </td></tr>
  <tr><td style="padding:6px 28px 18px;text-align:center;font-size:20px;color:#18181b;"><span style="text-decoration:line-through;color:#a1a1aa;font-size:15px;">$120</span> &nbsp;<strong>$89</strong></td></tr>
  <tr><td style="padding:0 28px 12px;text-align:center;">${bulletproofCtaHtml('Buy now')}</td></tr>
  <tr><td style="padding:0 28px 24px;text-align:center;font-size:12px;color:#a1a1aa;">Free shipping over $50 · 30-day easy returns</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;align-items:center;gap:3px;height:100%;padding:6px;background:#fafafa;">
  <div style="width:24%;height:5%;background:#d4d4d8;border-radius:2px;"></div>
  <div style="width:32%;height:5%;background:#fef3c7;border-radius:999px;"></div>
  <div style="width:90%;height:34%;background:#e4e4e7;border-radius:3px;"></div>
  <div style="width:62%;height:8%;background:#18181b;border-radius:2px;"></div>
  <div style="width:40%;height:4%;background:#f59e0b;border-radius:2px;"></div>
  <div style="width:75%;height:4%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="width:75%;height:4%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="width:45%;height:10%;background:#e85d4c;border-radius:3px;"></div>
</div>`,
  },
  {
    id: 'welcome-onboarding',
    label: 'Welcome',
    description: 'Lifestyle hero, brand pillars, welcome code — onboarding new subscribers.',
    category: 'ecommerce',
    layoutPrompt:
      ECOMMERCE_LAYOUT_PREFIX +
      'Use a brand wordmark header, a large lifestyle hero image (mood / brand world, not a product card), warm welcome headline ("Welcome to [Brand]" or similar), 2–3 sentences of brand intro, a 3-column row of brand pillars/values (icon + label + one-line description — e.g. Sustainable · Free shipping · 30-day returns), a dashed bordered welcome code box (e.g. WELCOME10 — 10% off your first order), primary Start shopping CTA, then footer. Tone: warm and inviting, not pushy or discount-led.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:0;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:240px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;">Lifestyle hero image</td></tr></table></td></tr>
  <tr><td style="padding:32px 28px 8px;text-align:center;"><h1 style="margin:0;font-size:28px;line-height:1.2;color:#18181b;font-weight:700;">Welcome to the family</h1></td></tr>
  <tr><td style="padding:8px 28px 24px;text-align:center;"><p style="margin:0;font-size:15px;line-height:1.6;color:#52525b;">We&apos;re so glad you&apos;re here. Here&apos;s a little something to get you started — plus everything you need to know about us.</p></td></tr>
  <tr><td style="padding:0 16px 16px;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td width="33%" style="padding:12px 10px;text-align:center;vertical-align:top;">
        <div style="font-size:13px;font-weight:700;color:#18181b;margin-bottom:6px;letter-spacing:0.04em;">SUSTAINABLE</div>
        <div style="font-size:12px;color:#52525b;line-height:1.5;">Responsibly sourced, made to last.</div>
      </td>
      <td width="33%" style="padding:12px 10px;text-align:center;vertical-align:top;border-left:1px solid #e4e4e7;border-right:1px solid #e4e4e7;">
        <div style="font-size:13px;font-weight:700;color:#18181b;margin-bottom:6px;letter-spacing:0.04em;">FREE SHIPPING</div>
        <div style="font-size:12px;color:#52525b;line-height:1.5;">On all orders over $50.</div>
      </td>
      <td width="34%" style="padding:12px 10px;text-align:center;vertical-align:top;">
        <div style="font-size:13px;font-weight:700;color:#18181b;margin-bottom:6px;letter-spacing:0.04em;">EASY RETURNS</div>
        <div style="font-size:12px;color:#52525b;line-height:1.5;">30 days, no questions asked.</div>
      </td>
    </tr></table>
  </td></tr>
  ${promoCodeBox('WELCOME10', '10% off your first order')}
  <tr><td style="padding:4px 28px 32px;text-align:center;">${bulletproofCtaHtml('Start shopping')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;gap:3px;height:100%;padding:6px;background:#fafafa;">
  <div style="height:5%;background:#d4d4d8;border-radius:2px;margin:0 35%;"></div>
  <div style="height:28%;background:#e4e4e7;border-radius:3px;"></div>
  <div style="height:9%;background:#18181b;border-radius:2px;margin:0 18%;"></div>
  <div style="height:5%;background:#e4e4e7;border-radius:2px;margin:0 14%;"></div>
  <div style="display:flex;gap:3px;height:14%;"><div style="flex:1;background:#e4e4e7;border-radius:2px;"></div><div style="flex:1;background:#e4e4e7;border-radius:2px;"></div><div style="flex:1;background:#e4e4e7;border-radius:2px;"></div></div>
  <div style="height:13%;border:2px dashed #d4d4d8;border-radius:3px;background:#fafafa;margin:0 16%;"></div>
  <div style="height:9%;background:#e85d4c;border-radius:3px;margin:0 28%;"></div>
</div>`,
  },
  {
    id: 'abandoned-cart',
    label: 'Abandoned cart',
    description: 'Cart reminder with product recap, reassurance, and checkout CTA.',
    category: 'ecommerce',
    metadata: {
      audienceType: 'Shoppers who added products to cart but did not complete purchase',
      campaignGoal: 'Bring shoppers back to checkout without feeling pushy',
      campaignType: 'abandoned_cart',
      recommendedStyles: ['direct', 'minimal', 'playful'],
      requiredSections: ['Reminder headline', 'Cart item summary', 'Trust/reassurance row', 'Checkout CTA', 'Footer'],
      optionalSections: ['Limited-time incentive', 'Support link', 'Return policy note'],
      defaultCtaIntent: 'complete_checkout',
      copyDensity: 'short',
      imageStrategy: 'thumbnail',
    },
    layoutPrompt:
      ECOMMERCE_LAYOUT_PREFIX +
      'Use an abandoned-cart structure: friendly reminder headline, compact cart item row with image placeholder, product name and price, short reassurance line, one primary "Complete checkout" CTA, and trust notes for shipping, returns, or support. Avoid sounding accusatory.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:28px 28px 8px;text-align:center;"><h1 style="margin:0;font-size:28px;line-height:1.2;color:#18181b;">Still thinking it over?</h1></td></tr>
  <tr><td style="padding:6px 34px 22px;text-align:center;font-size:15px;line-height:1.6;color:#52525b;">Your cart is saved. Come back when you're ready.</td></tr>
  <tr><td style="padding:0 28px 20px;"><table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e4e4e7;border-radius:8px;"><tr><td width="120" style="padding:14px;"><table width="96" cellpadding="0" cellspacing="0"><tr><td style="height:96px;background:#e4e4e7;text-align:center;color:#71717a;font-size:11px;border-radius:6px;">Item</td></tr></table></td><td style="padding:14px 14px 14px 0;font-size:14px;line-height:1.6;color:#52525b;"><strong style="color:#18181b;">[Product name]</strong><br/>[Variant or benefit]<br/><strong style="color:#18181b;">[Price]</strong></td></tr></table></td></tr>
  <tr><td style="padding:0 28px 14px;text-align:center;">${bulletproofCtaHtml('Complete checkout')}</td></tr>
  <tr><td style="padding:0 28px 28px;text-align:center;font-size:12px;color:#71717a;">Free shipping threshold · Easy returns · Secure checkout</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
  {
    id: 'back-in-stock',
    label: 'Back in stock',
    description: 'Restock alert with urgency, product highlight, and buy CTA.',
    category: 'ecommerce',
    metadata: {
      audienceType: 'Customers waiting for a product restock',
      campaignGoal: 'Notify interested shoppers and convert demand quickly',
      campaignType: 'back_in_stock',
      recommendedStyles: ['direct', 'bold', 'minimal'],
      requiredSections: ['Restock label', 'Product hero', 'Availability message', 'Buy CTA', 'Footer'],
      optionalSections: ['Low-stock note', 'Reviews', 'Shipping promise'],
      defaultCtaIntent: 'buy_now',
      copyDensity: 'short',
      imageStrategy: 'hero',
    },
    layoutPrompt:
      ECOMMERCE_LAYOUT_PREFIX +
      'Use a back-in-stock structure: bold restock label, hero product image, product name, short availability message, optional low-stock note, and one primary "Shop now" CTA. Make urgency helpful, not spammy.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:0;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:230px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;">Product hero image</td></tr></table></td></tr>
  <tr><td style="padding:28px 28px 8px;text-align:center;"><span style="font-size:11px;font-weight:800;color:#16a34a;letter-spacing:0.12em;">BACK IN STOCK</span></td></tr>
  <tr><td style="padding:0 28px 8px;text-align:center;"><h1 style="margin:0;font-size:30px;line-height:1.18;color:#18181b;">[Product] is available again</h1></td></tr>
  <tr><td style="padding:6px 34px 22px;text-align:center;font-size:15px;line-height:1.6;color:#52525b;">The item you asked about is back. Sizes and quantities may move quickly.</td></tr>
  <tr><td style="padding:0 28px 34px;text-align:center;">${bulletproofCtaHtml('Shop now', '#', '#16a34a')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },

  // ---------------------------------------------------------------------------
  // GENERAL — works for any vertical
  // ---------------------------------------------------------------------------
  {
    id: 'hero-cta',
    label: 'Hero + CTA',
    description: 'Full-width hero, headline, single primary button — classic promo or welcome.',
    category: 'general',
    layoutPrompt:
      GENERAL_LAYOUT_PREFIX +
      'Use a brand wordmark header, full-width hero image band, one strong centered headline, 1–2 short supporting lines, a single prominent CTA button, then a minimal footer. Keep the story linear: attention → value → action. Works for promos, welcome blasts, single-message announcements.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:0;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:220px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;">Hero image</td></tr></table></td></tr>
  <tr><td style="padding:32px 28px 8px;text-align:center;"><h1 style="margin:0;font-size:28px;line-height:1.25;color:#18181b;font-weight:700;">Headline goes here</h1></td></tr>
  <tr><td style="padding:8px 28px 28px;text-align:center;"><p style="margin:0;font-size:15px;line-height:1.6;color:#52525b;">Supporting copy that explains the offer or message in one or two sentences.</p></td></tr>
  <tr><td style="padding:0 28px 36px;text-align:center;">${bulletproofCtaHtml('Primary CTA')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;gap:3px;height:100%;padding:6px;background:#fafafa;font-size:0;">
  <div style="height:5%;background:#d4d4d8;border-radius:2px;margin:0 35%;"></div>
  <div style="height:36%;background:#e4e4e7;border-radius:3px;"></div>
  <div style="height:10%;background:#18181b;border-radius:2px;margin:6px 12% 0;"></div>
  <div style="height:5%;background:#e4e4e7;border-radius:2px;margin:0 18%;"></div>
  <div style="height:12%;background:#e85d4c;border-radius:3px;margin:6px 30% 0;"></div>
  <div style="height:4%;background:#e4e4e7;border-radius:2px;margin-top:auto;"></div>
</div>`,
  },

  // ---------------------------------------------------------------------------
  // SAAS / PRODUCT — feature launches, updates, announcements
  // ---------------------------------------------------------------------------
  {
    id: 'launch-announcement',
    label: 'Launch announcement',
    description: 'Eyebrow, headline, bullet features, screenshot — product or feature launches.',
    category: 'product',
    metadata: {
      audienceType: 'Product users, waitlist subscribers, or prospects',
      campaignGoal: 'Announce a new product or feature and drive readers to try it',
      campaignType: 'product_launch',
      recommendedStyles: ['direct', 'minimal', 'bold'],
      requiredSections: ['Brand header', 'Launch eyebrow', 'Benefit-led headline', 'Feature bullets', 'Product visual', 'Primary CTA', 'Footer'],
      optionalSections: ['Launch date', 'Social proof', 'Secondary documentation link'],
      defaultCtaIntent: 'try_product',
      copyDensity: 'medium',
      imageStrategy: 'hero',
    },
    layoutPrompt:
      PRODUCT_LAYOUT_PREFIX +
      'Use a centered layout: brand wordmark, small eyebrow label (e.g. "JUST LAUNCHED" or "NEW"), bold headline, 3 short benefit/feature bullets, a hero visual block (product screenshot or illustration), primary CTA, then footer. Feels like a SaaS or product release announcement — informational, not transactional.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:0 28px 8px;text-align:center;"><span style="display:inline-block;padding:4px 12px;background:#dbeafe;color:#1d4ed8;font-size:11px;font-weight:700;border-radius:999px;letter-spacing:0.08em;">JUST LAUNCHED</span></td></tr>
  <tr><td style="padding:8px 28px 12px;text-align:center;"><h1 style="margin:0;font-size:26px;line-height:1.3;color:#18181b;font-weight:700;">Introducing something big</h1></td></tr>
  <tr><td style="padding:0 28px 16px;text-align:center;font-size:14px;color:#52525b;line-height:1.6;">A short paragraph that sets up what's new and why it matters to your users.</td></tr>
  <tr><td style="padding:0 28px 16px;">
    <table width="100%" cellpadding="0" cellspacing="0" style="font-size:14px;color:#52525b;line-height:1.7;">
      <tr><td style="padding:6px 0;">✓ &nbsp; Benefit one explained briefly</td></tr>
      <tr><td style="padding:6px 0;">✓ &nbsp; Benefit two explained briefly</td></tr>
      <tr><td style="padding:6px 0;">✓ &nbsp; Benefit three explained briefly</td></tr>
    </table>
  </td></tr>
  <tr><td style="padding:0 28px 20px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:180px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;border-radius:6px;">Product screenshot / illustration</td></tr></table></td></tr>
  <tr><td style="padding:0 28px 32px;text-align:center;">${bulletproofCtaHtml('Try it now')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;align-items:center;gap:3px;height:100%;padding:6px;background:#fafafa;">
  <div style="width:24%;height:5%;background:#d4d4d8;border-radius:2px;"></div>
  <div style="width:30%;height:5%;background:#dbeafe;border-radius:999px;"></div>
  <div style="width:60%;height:9%;background:#18181b;border-radius:2px;"></div>
  <div style="width:80%;height:4%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="width:75%;height:4%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="width:75%;height:4%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="width:90%;height:26%;background:#e4e4e7;border-radius:3px;margin:2px 0;"></div>
  <div style="width:45%;height:10%;background:#e85d4c;border-radius:3px;"></div>
</div>`,
  },
  {
    id: 'product-hunt-launch',
    label: 'Product Hunt',
    description: 'Launch-day pitch with proof, urgency, and support CTA.',
    category: 'product',
    metadata: {
      audienceType: 'Existing users, waitlist subscribers, indie maker audience',
      campaignGoal: 'Drive launch-day visits, support, comments, or signups',
      campaignType: 'product_hunt_launch',
      recommendedStyles: ['bold', 'direct', 'minimal'],
      requiredSections: ['Launch badge', 'One-line product promise', 'Problem/value bullets', 'Social proof or maker note', 'Support CTA', 'Footer'],
      optionalSections: ['Launch-day bonus', 'Screenshot placeholder', 'Founder signature'],
      defaultCtaIntent: 'support_launch',
      copyDensity: 'medium',
      imageStrategy: 'hero',
    },
    layoutPrompt:
      PRODUCT_LAYOUT_PREFIX +
      'Use a launch-day structure: compact brand header, bright launch badge, punchy product promise, 2-3 outcome bullets, screenshot placeholder, short founder note, and one primary CTA such as "Support us today" or "View the launch". Keep it energetic and founder-led.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:8px 28px 10px;text-align:center;"><span style="display:inline-block;padding:5px 12px;background:#ffedd5;color:#c2410c;font-size:11px;font-weight:800;border-radius:999px;letter-spacing:0.08em;">LAUNCH DAY</span></td></tr>
  <tr><td style="padding:6px 28px 8px;text-align:center;"><h1 style="margin:0;font-size:32px;line-height:1.12;color:#18181b;font-weight:800;">[Product] is live today</h1></td></tr>
  <tr><td style="padding:6px 34px 18px;text-align:center;font-size:15px;line-height:1.6;color:#52525b;">A crisp one-line promise for who it helps and what painful workflow it improves.</td></tr>
  <tr><td style="padding:0 28px 18px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:180px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;border-radius:8px;">Product screenshot</td></tr></table></td></tr>
  <tr><td style="padding:0 36px 18px;font-size:14px;line-height:1.7;color:#52525b;">✓ Built for [audience]<br/>✓ Helps you [key outcome]<br/>✓ Ships with [notable feature or launch bonus]</td></tr>
  <tr><td style="padding:0 34px 18px;font-size:14px;line-height:1.6;color:#52525b;border-left:4px solid #ff6b35;">A short founder note that makes the launch feel personal and timely.</td></tr>
  <tr><td style="padding:0 28px 32px;text-align:center;">${bulletproofCtaHtml('Support the launch', '#', '#ff6b35')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
  {
    id: 'feature-update',
    label: 'Feature update',
    description: 'Focused product improvement with benefits and try-it CTA.',
    category: 'product',
    metadata: {
      audienceType: 'Active users, trial users, product subscribers',
      campaignGoal: 'Explain what changed and get users to try the feature',
      campaignType: 'feature_update',
      recommendedStyles: ['direct', 'minimal'],
      requiredSections: ['Product header', 'New feature label', 'Benefit headline', 'Before/after bullets', 'Product visual', 'Try CTA', 'Footer'],
      optionalSections: ['Docs link', 'Customer quote', 'Availability note'],
      defaultCtaIntent: 'try_feature',
      copyDensity: 'medium',
      imageStrategy: 'hero',
    },
    layoutPrompt:
      PRODUCT_LAYOUT_PREFIX +
      'Use a practical feature-update layout: small "NEW" eyebrow, benefit-led headline, one short paragraph, 3 bullets that translate features into outcomes, screenshot placeholder, primary CTA to try the feature, and optional docs text link.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:8px 28px;text-align:left;"><span style="font-size:11px;font-weight:800;color:#2563eb;letter-spacing:0.1em;">NEW FEATURE</span></td></tr>
  <tr><td style="padding:4px 28px 8px;"><h1 style="margin:0;font-size:28px;line-height:1.2;color:#18181b;">[Feature] helps you [outcome]</h1></td></tr>
  <tr><td style="padding:4px 28px 18px;font-size:15px;line-height:1.6;color:#52525b;">Explain the release in plain language and connect it to a real workflow.</td></tr>
  <tr><td style="padding:0 28px 18px;font-size:14px;line-height:1.8;color:#52525b;">✓ Before: [old friction]<br/>✓ Now: [new capability]<br/>✓ Best for: [specific use case]</td></tr>
  <tr><td style="padding:0 28px 22px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:170px;background:#e4e4e7;text-align:center;color:#71717a;font-size:12px;border-radius:8px;">Screenshot or workflow image</td></tr></table></td></tr>
  <tr><td style="padding:0 28px 34px;text-align:center;">${bulletproofCtaHtml('Try the feature', '#', '#2563eb')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
  {
    id: 'beta-invite',
    label: 'Beta invite',
    description: 'Early-access invitation with value, credibility, and signup CTA.',
    category: 'product',
    metadata: {
      audienceType: 'Waitlist subscribers, early adopters, friendly prospects',
      campaignGoal: 'Invite readers to join a beta or private preview',
      campaignType: 'beta_invite',
      recommendedStyles: ['minimal', 'direct', 'luxury'],
      requiredSections: ['Invitation headline', 'Who it is for', 'What beta users get', 'Simple CTA', 'Footer'],
      optionalSections: ['Limited seats note', 'Founder note', 'Feedback expectation'],
      defaultCtaIntent: 'join_beta',
      copyDensity: 'medium',
      imageStrategy: 'none',
    },
    layoutPrompt:
      PRODUCT_LAYOUT_PREFIX +
      'Use an invitation layout: personal opening, clear beta value, who should join, what participants get, low-friction CTA, and a warm founder-style signoff. Avoid hype; make it feel selective and useful.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:34px 34px 8px;"><div style="font-size:12px;font-weight:800;color:#71717a;letter-spacing:0.12em;">PRIVATE BETA</div></td></tr>
  <tr><td style="padding:0 34px 14px;"><h1 style="margin:0;font-size:30px;line-height:1.2;color:#18181b;">You're invited to try [Product]</h1></td></tr>
  <tr><td style="padding:0 34px 20px;font-size:15px;line-height:1.7;color:#52525b;">A concise note explaining why this reader is being invited and what early access helps them do.</td></tr>
  <tr><td style="padding:0 34px 20px;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border:1px solid #e4e4e7;border-radius:8px;"><tr><td style="padding:18px 20px;font-size:14px;line-height:1.8;color:#52525b;">✓ Early access to [core feature]<br/>✓ A direct line for feedback<br/>✓ Help shape what ships next</td></tr></table></td></tr>
  <tr><td style="padding:0 34px 34px;text-align:left;">${bulletproofCtaHtml('Join the beta', '#', '#18181b')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },

  // ---------------------------------------------------------------------------
  // CONTENT — newsletters, digests, editorial
  // ---------------------------------------------------------------------------
  {
    id: 'newsletter-digest',
    label: 'Newsletter digest',
    description: 'Featured story plus stacked article rows — content roundups and blogs.',
    category: 'newsletter',
    metadata: {
      audienceType: 'Newsletter subscribers and readers',
      campaignGoal: 'Help readers scan and click the most useful content',
      campaignType: 'newsletter_digest',
      recommendedStyles: ['minimal', 'direct', 'luxury'],
      requiredSections: ['Issue header', 'Featured story', 'Secondary story rows', 'Footer with preferences'],
      optionalSections: ['Sponsor slot', 'Editor note', 'Category labels'],
      defaultCtaIntent: 'read_more',
      copyDensity: 'dense',
      imageStrategy: 'thumbnail',
    },
    layoutPrompt:
      NEWSLETTER_LAYOUT_PREFIX +
      'Use a newsletter digest structure: header with brand wordmark + issue/date, one large featured story (image, headline, excerpt, read link), a hairline divider, then 2–3 compact rows (small thumbnail left, title + 1-line excerpt right). End with a footer that includes unsubscribe + preferences. Tone: editorial, scannable, not promotional.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  <tr><td style="padding:24px 24px 16px;border-bottom:1px solid #e4e4e7;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td style="font-size:16px;font-weight:700;color:#18181b;letter-spacing:0.04em;">WEEKLY DIGEST</td>
      <td style="text-align:right;font-size:12px;color:#a1a1aa;">Issue #42 · March 2026</td>
    </tr></table>
  </td></tr>
  <tr><td style="padding:20px 24px 8px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="height:160px;background:#e4e4e7;text-align:center;font-size:11px;color:#71717a;border-radius:6px;">Featured image</td></tr></table></td></tr>
  <tr><td style="padding:8px 24px 4px;"><h2 style="margin:0;font-size:22px;color:#18181b;font-weight:700;">Featured story headline</h2></td></tr>
  <tr><td style="padding:4px 24px 12px;font-size:14px;line-height:1.6;color:#52525b;">Short excerpt for the lead article that hooks the reader and invites a click.</td></tr>
  <tr><td style="padding:0 24px 20px;font-size:13px;"><a href="#" style="color:#e85d4c;font-weight:600;">Read more →</a></td></tr>
  <tr><td style="padding:0 24px;"><hr style="border:none;border-top:1px solid #e4e4e7;margin:0;"/></td></tr>
  <tr><td style="padding:16px 24px;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td width="96" style="vertical-align:top;padding-right:14px;"><table width="96" cellpadding="0" cellspacing="0"><tr><td style="height:72px;background:#e4e4e7;font-size:10px;color:#71717a;text-align:center;border-radius:4px;">Thumb</td></tr></table></td>
      <td style="vertical-align:top;">
        <div style="font-size:15px;font-weight:600;color:#18181b;margin-bottom:4px;">Article title two</div>
        <div style="font-size:13px;color:#52525b;line-height:1.5;">Brief teaser text for the second story.</div>
        <div style="margin-top:6px;font-size:12px;"><a href="#" style="color:#e85d4c;font-weight:600;">Read →</a></div>
      </td>
    </tr></table>
  </td></tr>
  <tr><td style="padding:8px 24px 20px;">
    <table width="100%" cellpadding="0" cellspacing="0"><tr>
      <td width="96" style="vertical-align:top;padding-right:14px;"><table width="96" cellpadding="0" cellspacing="0"><tr><td style="height:72px;background:#e4e4e7;font-size:10px;color:#71717a;text-align:center;border-radius:4px;">Thumb</td></tr></table></td>
      <td style="vertical-align:top;">
        <div style="font-size:15px;font-weight:600;color:#18181b;margin-bottom:4px;">Article title three</div>
        <div style="font-size:13px;color:#52525b;line-height:1.5;">Brief teaser text for the third story.</div>
        <div style="margin-top:6px;font-size:12px;"><a href="#" style="color:#e85d4c;font-weight:600;">Read →</a></div>
      </td>
    </tr></table>
  </td></tr>
  ${FOOTER}
</table>`),
    previewHtml: `
<div style="display:flex;flex-direction:column;gap:3px;height:100%;padding:6px;background:#fafafa;">
  <div style="height:7%;background:#d4d4d8;border-radius:2px;"></div>
  <div style="height:28%;background:#e4e4e7;border-radius:3px;"></div>
  <div style="height:7%;background:#18181b;border-radius:2px;width:70%;"></div>
  <div style="height:4%;background:#e4e4e7;border-radius:2px;"></div>
  <div style="display:flex;gap:3px;height:18%;"><div style="width:28%;background:#e4e4e7;border-radius:2px;"></div><div style="flex:1;background:#e4e4e7;border-radius:2px;"></div></div>
  <div style="display:flex;gap:3px;height:18%;"><div style="width:28%;background:#e4e4e7;border-radius:2px;"></div><div style="flex:1;background:#e4e4e7;border-radius:2px;"></div></div>
</div>`,
  },
  {
    id: 'daily-brief',
    label: 'Daily brief',
    description: 'Text-first daily update with 3-5 concise items and takeaway.',
    category: 'newsletter',
    metadata: {
      audienceType: 'Busy readers who want a concise daily update',
      campaignGoal: 'Summarize important updates quickly and build a reading habit',
      campaignType: 'daily_brief',
      recommendedStyles: ['minimal', 'direct'],
      requiredSections: ['Date header', 'One-line summary', '3-5 brief items', 'Key takeaway', 'Footer'],
      optionalSections: ['Read more links', 'Sponsor line', 'Tomorrow teaser'],
      defaultCtaIntent: 'read_full_update',
      copyDensity: 'dense',
      imageStrategy: 'none',
    },
    layoutPrompt:
      NEWSLETTER_LAYOUT_PREFIX +
      'Use a text-first daily brief layout: compact issue/date header, one sentence lead, 3-5 numbered or separated brief items with short headlines and one-line summaries, then a key takeaway box. Keep imagery minimal or absent.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  <tr><td style="padding:24px 28px 14px;border-bottom:1px solid #e4e4e7;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="font-size:15px;font-weight:800;color:#18181b;">[Newsletter]</td><td style="text-align:right;font-size:12px;color:#71717a;">[Issue/date]</td></tr></table></td></tr>
  <tr><td style="padding:22px 28px 10px;"><h1 style="margin:0;font-size:24px;line-height:1.25;color:#18181b;">Today's brief</h1></td></tr>
  <tr><td style="padding:0 28px 18px;font-size:15px;line-height:1.6;color:#52525b;">One sharp sentence that tells readers what matters today.</td></tr>
  <tr><td style="padding:0 28px;"><table width="100%" cellpadding="0" cellspacing="0" style="font-size:14px;line-height:1.6;color:#52525b;">
    <tr><td style="padding:14px 0;border-top:1px solid #e4e4e7;"><strong style="color:#18181b;">1. [Headline]</strong><br/>One-line summary and why it matters.</td></tr>
    <tr><td style="padding:14px 0;border-top:1px solid #e4e4e7;"><strong style="color:#18181b;">2. [Headline]</strong><br/>One-line summary and why it matters.</td></tr>
    <tr><td style="padding:14px 0;border-top:1px solid #e4e4e7;"><strong style="color:#18181b;">3. [Headline]</strong><br/>One-line summary and why it matters.</td></tr>
  </table></td></tr>
  <tr><td style="padding:8px 28px 26px;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#f8fafc;border-left:4px solid #18181b;"><tr><td style="padding:14px 16px;font-size:14px;line-height:1.6;color:#52525b;"><strong style="color:#18181b;">Key takeaway:</strong> [One useful conclusion]</td></tr></table></td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
  {
    id: 'curated-links',
    label: 'Curated links',
    description: 'Sectioned link roundup with short editorial notes.',
    category: 'newsletter',
    metadata: {
      audienceType: 'Newsletter readers who expect useful links and commentary',
      campaignGoal: 'Curate resources and drive clicks to selected links',
      campaignType: 'curated_links',
      recommendedStyles: ['minimal', 'direct', 'playful'],
      requiredSections: ['Issue header', 'Editor note', 'Curated link sections', 'Footer'],
      optionalSections: ['Sponsor slot', 'Community question', 'Reply CTA'],
      defaultCtaIntent: 'read_curated_link',
      copyDensity: 'dense',
      imageStrategy: 'none',
    },
    layoutPrompt:
      NEWSLETTER_LAYOUT_PREFIX +
      'Use a curated-links layout: short editor note, section headers, 4-6 link rows with title, one-line commentary, and a small "Read" link. If a sponsor is mentioned, separate it clearly from editorial content.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  <tr><td style="padding:24px 28px 14px;border-bottom:1px solid #e4e4e7;font-size:16px;font-weight:800;color:#18181b;">[Newsletter] · Curated links</td></tr>
  <tr><td style="padding:22px 28px 16px;font-size:15px;line-height:1.7;color:#52525b;">A short editor note explaining the theme of this issue.</td></tr>
  <tr><td style="padding:0 28px 8px;font-size:12px;font-weight:800;color:#71717a;letter-spacing:0.1em;">THIS WEEK</td></tr>
  <tr><td style="padding:0 28px 22px;"><table width="100%" cellpadding="0" cellspacing="0" style="font-size:14px;line-height:1.6;color:#52525b;">
    <tr><td style="padding:12px 0;border-top:1px solid #e4e4e7;"><strong style="color:#18181b;">[Link title]</strong><br/>Why this is worth the reader's time. <a href="#" style="color:#e85d4c;font-weight:600;">Read</a></td></tr>
    <tr><td style="padding:12px 0;border-top:1px solid #e4e4e7;"><strong style="color:#18181b;">[Link title]</strong><br/>Why this is worth the reader's time. <a href="#" style="color:#e85d4c;font-weight:600;">Read</a></td></tr>
    <tr><td style="padding:12px 0;border-top:1px solid #e4e4e7;"><strong style="color:#18181b;">[Link title]</strong><br/>Why this is worth the reader's time. <a href="#" style="color:#e85d4c;font-weight:600;">Read</a></td></tr>
  </table></td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
  {
    id: 'founder-build-log',
    label: 'Build log',
    description: 'Personal founder update with shipped, learned, next sections.',
    category: 'creator',
    metadata: {
      audienceType: 'Followers, customers, investors, or community members',
      campaignGoal: 'Build trust through a personal progress update',
      campaignType: 'founder_update',
      recommendedStyles: ['minimal', 'direct'],
      requiredSections: ['Personal opening', 'Shipped section', 'Learned section', 'Next section', 'Reply CTA', 'Footer'],
      optionalSections: ['Metric', 'Ask for feedback', 'Behind-the-scenes image'],
      defaultCtaIntent: 'reply_or_follow_progress',
      copyDensity: 'medium',
      imageStrategy: 'none',
    },
    layoutPrompt:
      NEWSLETTER_LAYOUT_PREFIX +
      'Use a personal build-log layout: warm opening note, three clear sections titled Shipped / Learned / Next, one concrete metric or moment if available, and a soft CTA to reply, try the product, or follow along.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  <tr><td style="padding:28px 34px 8px;font-size:15px;font-weight:800;color:#18181b;">[Founder or brand]</td></tr>
  <tr><td style="padding:10px 34px 12px;"><h1 style="margin:0;font-size:28px;line-height:1.22;color:#18181b;">A quick build update</h1></td></tr>
  <tr><td style="padding:0 34px 20px;font-size:15px;line-height:1.7;color:#52525b;">A short personal note that frames what changed and why it matters.</td></tr>
  <tr><td style="padding:0 34px 24px;font-size:14px;line-height:1.8;color:#52525b;"><strong style="color:#18181b;">Shipped</strong><br/>[What shipped]<br/><br/><strong style="color:#18181b;">Learned</strong><br/>[What you learned]<br/><br/><strong style="color:#18181b;">Next</strong><br/>[What happens next]</td></tr>
  <tr><td style="padding:0 34px 34px;text-align:left;">${bulletproofCtaHtml('Reply with feedback', '#', '#18181b')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
  {
    id: 'webinar-invite',
    label: 'Event invite',
    description: 'Webinar or workshop invite with agenda and registration CTA.',
    category: 'course',
    metadata: {
      audienceType: 'Prospects, students, community members, or leads',
      campaignGoal: 'Drive registration for a live event, webinar, or workshop',
      campaignType: 'event_invite',
      recommendedStyles: ['direct', 'bold', 'minimal'],
      requiredSections: ['Event label', 'Topic headline', 'Date/time details', 'Agenda bullets', 'Register CTA', 'Footer'],
      optionalSections: ['Speaker bio', 'Who should attend', 'Replay availability'],
      defaultCtaIntent: 'register',
      copyDensity: 'medium',
      imageStrategy: 'none',
    },
    layoutPrompt:
      COURSE_LAYOUT_PREFIX +
      'Use an event-invite layout: event type label, outcome-focused headline, date/time block, 3 agenda bullets, speaker or host note if provided, and one primary register CTA.',
    starterHtml: WRAPPER(`
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;background:#ffffff;">
  ${BRAND_HEADER}
  <tr><td style="padding:28px 28px 8px;text-align:center;"><span style="font-size:11px;font-weight:800;color:#7c3aed;letter-spacing:0.12em;">LIVE WORKSHOP</span></td></tr>
  <tr><td style="padding:0 32px 12px;text-align:center;"><h1 style="margin:0;font-size:30px;line-height:1.18;color:#18181b;">Learn how to [desired outcome]</h1></td></tr>
  <tr><td style="padding:0 34px 18px;text-align:center;font-size:15px;line-height:1.6;color:#52525b;">[Date] · [Time] · Hosted by [Name]</td></tr>
  <tr><td style="padding:0 34px 22px;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#fafafa;border:1px solid #e4e4e7;border-radius:8px;"><tr><td style="padding:18px 20px;font-size:14px;line-height:1.8;color:#52525b;">✓ [Agenda point one]<br/>✓ [Agenda point two]<br/>✓ [Agenda point three]</td></tr></table></td></tr>
  <tr><td style="padding:0 28px 34px;text-align:center;">${bulletproofCtaHtml('Register now', '#', '#7c3aed')}</td></tr>
  ${FOOTER}
</table>`),
    previewHtml: SIMPLE_PREVIEW,
  },
];

export const DEFAULT_LAYOUT_TEMPLATE_ID = 'flash-sale';

export function getLayoutTemplateById(id: string): EmailLayoutTemplate | undefined {
  if (id === LAYOUT_TEMPLATE_NONE_ID) return undefined;
  return EMAIL_LAYOUT_TEMPLATES.find((t) => t.id === id);
}

export function getLayoutTemplateLabel(id: string): string {
  if (id === LAYOUT_TEMPLATE_NONE_ID) return 'Custom';
  return getLayoutTemplateById(id)?.label ?? 'Custom';
}

/** Filtered list of templates for a given category (or all). */
export function getTemplatesByCategory(
  category: TemplateCategory | 'all',
): EmailLayoutTemplate[] {
  if (category === 'all') return EMAIL_LAYOUT_TEMPLATES;
  return EMAIL_LAYOUT_TEMPLATES.filter((t) => t.category === category);
}
